import React, { useCallback, useState } from 'react';
import {
    Anchor,
    Box,
    Button,
    DataTable,
    Heading,
    Page,
    PageContent,
    Pagination,
    Select,
    Text,
    TextInput,
} from 'grommet';
import { API } from '../../api';
import { Utils } from '../../helpers/Utils';
import { RowsPerPageSelect } from '../../components/RowsPerPageSelect';
import {
    resolveSortField,
    usePaginatedSort,
} from '../../hooks/usePaginatedSort';
import { usePaginatedFetch } from '../../hooks/usePaginatedFetch';
import QueryString from 'qs';
import PropTypes from 'prop-types';
import { useDebouncedCallback } from 'use-debounce';
import { useNavigate, useParams } from 'react-router-dom';

const SEARCH_DEBOUNCE_MS = 400;

// Tri-state filter: "Any" means the param is left off the request entirely,
// so the server applies no condition for it.
const FILTER_ANY = 'Any';
const FILTER_OPTIONS = [FILTER_ANY, 'Yes', 'No'];
const FILTER_VALUES = { Yes: true, No: false };

const filterParam = (option) => FILTER_VALUES[option];

const FilterSelect = ({ label, value, onChange }) => (
    <Box direction="row" align="center" gap="xsmall">
        <Text size="small">{label}</Text>
        <Select
            a11yTitle={label}
            options={FILTER_OPTIONS}
            value={value}
            onChange={({ option }) => onChange(option)}
            size="small"
        />
    </Box>
);

FilterSelect.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};

export const CompaniesDashboard = ({ showAdminActions = true }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const [contractClientFilter, setContractClientFilter] =
        useState(FILTER_ANY);
    const [reportClientFilter, setReportClientFilter] = useState(FILTER_ANY);
    const {
        page,
        pageSize,
        sort,
        totalItems,
        stepOptions,
        setPage,
        handleSort,
        handlePageChange,
        handlePageSizeChange,
        applyPagination,
    } = usePaginatedSort();

    // The input stays instant; only the debounced value drives a refetch.
    // Any filter change also drops back to page 1, since the current offset
    // may not exist in the narrowed set.
    const applySearchTerm = useDebouncedCallback((value) => {
        setAppliedSearchTerm(value);
        setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    const applyFilter = (setFilter) => (option) => {
        setFilter(option);
        setPage(1);
    };
    const navigate = useNavigate();
    const params = useParams();
    const companyBasePath = params.schoolSlug
        ? `/${params.schoolSlug}/companies`
        : '/companies';

    const fetchPage = useCallback(
        () =>
            loadCompanies({
                page,
                pageSize,
                onlyWithJobs: !showAdminActions,
                sort,
                search: appliedSearchTerm,
                isContractClient: filterParam(contractClientFilter),
                isReportClient: filterParam(reportClientFilter),
            }),
        [
            page,
            pageSize,
            showAdminActions,
            sort,
            appliedSearchTerm,
            contractClientFilter,
            reportClientFilter,
        ],
    );
    const { items: companies } = usePaginatedFetch({
        fetchPage,
        applyPagination,
    });

    const dataColumns = [
        {
            header: 'Company',
            property: 'name',
            render: (company) => (
                <Anchor
                    key={company.id}
                    href={`${companyBasePath}/${company.id}`}
                    label={company.name}
                />
            ),
            sortable: true,
        },
        showAdminActions
            ? {
                header: 'Status',
                property: 'isContractClient',
                render: (company) =>
                    company.isContractClient ? (
                        <Box
                            alignSelf="start"
                            background="enrich-green-light"
                            round="6px"
                            pad={{ vertical: 'xsmall', horizontal: 'small' }}>
                            <Text>Contract Client</Text>
                        </Box>
                    ) : (
                        ''
                    ),
                sortable: true,
            }
            : {
                header: 'Open Roles',
                property: 'openRoles',
                render: (company) => {
                    const jobs = company.jobs || [];
                    return jobs.filter(
                        (j) => j.status?.toLowerCase() !== 'closed',
                    ).length;
                },
            },
        showAdminActions && {
            header: 'Reports',
            property: 'isReportClient',
            render: (company) =>
                company.isReportClient ? (
                    <Box
                        alignSelf="start"
                        background="enrich-green-light"
                        round="6px"
                        pad={{ vertical: 'xsmall', horizontal: 'small' }}>
                        <Text>Reports On</Text>
                    </Box>
                ) : (
                    ''
                ),
            sortable: true,
        },
        showAdminActions && {
            header: 'Last Login',
            property: 'lastLogin',
            render: (company) =>
                company.lastLogin
                    ? new Date(company.lastLogin).toLocaleDateString()
                    : 'Never',
            sortable: true,
        },
    ].filter(Boolean);

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent>
                <Box gap="small">
                    <Box
                        direction="row-responsive"
                        justify="between"
                        pad={{ right: 'small' }}>
                        <Heading level="2">Companies</Heading>
                        {showAdminActions && (
                            <Box align="center" justify="center">
                                <Button
                                    primary
                                    size="large"
                                    label="Create New Company"
                                    onClick={() =>
                                        navigate('/companies/create')
                                    }
                                />
                            </Box>
                        )}
                    </Box>
                    {showAdminActions && (
                        <Box
                            direction="row-responsive"
                            align="center"
                            gap="medium"
                            pad={{ right: 'small', bottom: 'small' }}>
                            <Box width="medium">
                                <TextInput
                                    a11yTitle="Search companies"
                                    placeholder="Search by company name"
                                    value={searchTerm}
                                    onChange={(event) => {
                                        setSearchTerm(event.target.value);
                                        applySearchTerm(event.target.value);
                                    }}
                                />
                            </Box>
                            <FilterSelect
                                label="Contract client"
                                value={contractClientFilter}
                                onChange={applyFilter(setContractClientFilter)}
                            />
                            <FilterSelect
                                label="Reports"
                                value={reportClientFilter}
                                onChange={applyFilter(setReportClientFilter)}
                            />
                        </Box>
                    )}
                    <Box>
                        {companies.length === 0 ? (
                            <Box pad="medium" align="center">
                                <Text>No companies found</Text>
                            </Box>
                        ) : (
                            <DataTable
                                primaryKey="id"
                                sort={{ ...sort, external: true }}
                                onSort={handleSort}
                                data={companies}
                                columns={dataColumns}
                            />
                        )}
                    </Box>
                    <Box
                        direction="row-responsive"
                        align="center"
                        justify="between"
                        gap="small"
                        pad="small">
                        <RowsPerPageSelect
                            options={stepOptions}
                            value={pageSize}
                            onChange={handlePageSizeChange}
                        />
                        <Pagination
                            numberItems={totalItems}
                            page={page}
                            onChange={handlePageChange}
                            step={pageSize}
                            summary={true}
                        />
                    </Box>
                </Box>
            </PageContent>
        </Page>
    );
};

const SORT_FIELD_MAP = {
    name: 'name',
    isContractClient: 'is_contract_client',
    isReportClient: 'is_report_client',
    lastLogin: 'last_login',
};

// Admin view: true server-side sort on all columns (incl. the aggregated
// Last Login) via the custom /companies/list SQL endpoint, returning flat rows.
const loadAdminCompanies = async ({
    page,
    pageSize,
    sort,
    search,
    isContractClient,
    isReportClient,
} = {}) => {
    const sortBy = resolveSortField(sort, SORT_FIELD_MAP, 'name');
    const sortDir = sort?.direction ?? 'asc';
    const term = search?.trim();
    const query = QueryString.stringify(
        {
            page,
            pageSize,
            sortBy,
            sortDir,
            ...(term ? { search: term } : {}),
            ...(isContractClient !== undefined ? { isContractClient } : {}),
            ...(isReportClient !== undefined ? { isReportClient } : {}),
        },
        { encodeValuesOnly: true },
    );

    try {
        const response = await API.get(`/api/companies/list?${query}`);
        return { data: response.data.data, meta: response.data.meta };
    } catch (error) {
        console.log(error);
    }

    return { data: [], meta: { pagination: { total: 0, page } } };
};

// Non-admin view (school portal): companies that have jobs, with the
// client-derived Open Roles column. Uses the default find + relation populate.
const loadCompaniesWithJobs = async ({ page, pageSize, sort } = {}) => {
    const query = QueryString.stringify(
        {
            filters: { jobs: { id: { $notNull: true } } },
            populate: { jobs: { fields: ['id', 'status'] } },
            pagination: page && pageSize ? { page, pageSize } : undefined,
            sort: sort ? [`${sort.property}:${sort.direction}`] : ['name:asc'],
        },
        { encodeValuesOnly: true },
    );

    try {
        const response = await API.get(`/api/companies?${query}`);
        return {
            data: Utils.deepFlatten(response.data.data),
            meta: response.data.meta,
        };
    } catch (error) {
        console.log(error);
    }

    return { data: [], meta: { pagination: { total: 0, page } } };
};

const loadCompanies = ({ onlyWithJobs = false, ...rest } = {}) =>
    onlyWithJobs ? loadCompaniesWithJobs(rest) : loadAdminCompanies(rest);
